import { useState, useEffect, type FormEvent } from "react";
import { colors, fonts } from "./tokens";

/* ── Score card bar data for hero demo ── */
const BARS: { label: string; pct: number; color: string }[] = [
  { label: "Schedule", pct: 82, color: colors.red },
  { label: "Weather", pct: 61, color: colors.amber },
  { label: "Workforce", pct: 55, color: colors.amber },
  { label: "Compliance", pct: 28, color: colors.green },
  { label: "Equipment", pct: 74, color: colors.red },
];

/* ── Platform modules ── */
const MODULES = [
  {
    icon: "\u{1F6E1}",
    name: "Site Safety Intelligence",
    desc: "AI-powered risk scoring, real-time PPE and hazard detection, SIF-potential flagging, and automated safety recommendations fused from camera, weather, and schedule data.",
    status: "Pilot Ready",
    active: true,
  },
  {
    icon: "\u{1F4D0}",
    name: "Schedule Intelligence",
    desc: "Visual progress tracking against BIM/schedule baselines. Automatically detect delays, quantify percent-complete, and flag sequence violations.",
    status: "2027",
    active: false,
  },
  {
    icon: "\u{1F527}",
    name: "Equipment Intelligence",
    desc: "Track utilization, idle time, and maintenance signals across your fleet. Optimize crane placement and heavy equipment routing.",
    status: "2027",
    active: false,
  },
];

/* ── How-it-works steps ── */
const STEPS = [
  {
    num: "01",
    title: "Deploy edge nodes",
    desc: "Ruggedized camera nodes mount to existing infrastructure in 15 minutes. Repositionable as work phases change \u2014 designed for real construction sites, not labs.",
  },
  {
    num: "02",
    title: "Fuse all signals",
    desc: "On-device AI processes video in real-time. Cardo combines detections with weather forecasts, schedule phase data, crew composition, and compliance history.",
  },
  {
    num: "03",
    title: "Act on intelligence",
    desc: "Your safety team gets a daily risk score with prioritized, actionable recommendations \u2014 not just alerts, but exactly what to do and why, with OSHA context.",
  },
];

/* ── Base style for unstyled buttons (scroll actions) ── */
const btnReset: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  cursor: "pointer",
};

/* ── Shared input style ── */
const inputStyle: React.CSSProperties = {
  flex: 1,
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: 4,
  padding: "0.85rem 1rem",
  color: colors.text,
  fontFamily: fonts.body,
  fontSize: "0.9rem",
};

/* ── Floating score card in hero ── */
function ScoreCard() {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const dateStr = new Date()
    .toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    .toUpperCase();

  return (
    <div
      className="lp-hero-score anim-fade-in"
      style={{
        position: "absolute",
        right: "6%",
        top: "50%",
        transform: "translateY(-50%)",
        width: 320,
        animationDelay: "1s",
      }}
    >
      <div
        style={{
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: "1.5rem",
          fontFamily: fonts.mono,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "1.25rem",
            paddingBottom: "1rem",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <span
            style={{
              fontSize: "0.65rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: colors.muted,
            }}
          >
            Daily Site Risk Score
          </span>
          <span style={{ fontSize: "0.65rem", color: colors.muted }}>{dateStr}</span>
        </div>

        {/* Score value */}
        <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
          <div style={{ fontSize: "3.5rem", fontWeight: 700, color: colors.amber, lineHeight: 1 }}>
            67
          </div>
          <div
            style={{
              fontSize: "0.7rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: colors.amber,
              marginTop: "0.3rem",
            }}
          >
            &#9650; HIGH &mdash; Active Intervention Needed
          </div>
        </div>

        {/* Bars */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem" }}>
          {BARS.map((bar) => (
            <div
              key={bar.label}
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <span
                style={{
                  fontSize: "0.6rem",
                  color: colors.text2,
                  width: 70,
                  textAlign: "right",
                  letterSpacing: "0.03em",
                }}
              >
                {bar.label}
              </span>
              <div
                style={{
                  flex: 1,
                  height: 4,
                  background: colors.border,
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    borderRadius: 2,
                    background: bar.color,
                    width: animate ? `${bar.pct}%` : "0%",
                    transition: "width 1.5s ease",
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* SIF Warning */}
        <div
          style={{
            marginTop: "1.25rem",
            paddingTop: "1rem",
            borderTop: `1px solid ${colors.border}`,
          }}
        >
          <div
            style={{
              fontSize: "0.55rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              color: colors.red,
              marginBottom: "0.4rem",
            }}
          >
            &#9888; SIF Potential Detected
          </div>
          <div
            style={{
              fontFamily: fonts.body,
              fontSize: "0.75rem",
              color: colors.text2,
              lineHeight: 1.5,
            }}
          >
            Steel erection + crane ops + 38% new workers. Assign experienced mentors to all elevated
            work crews.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main landing page ── */
export function LandingPage() {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const [formState, setFormState] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    setFormState("sending");
    try {
      const formId = import.meta.env.VITE_FORMSPREE_ID;
      if (!formId) {
        setFormState("error");
        return;
      }
      const r = await fetch(`https://formspree.io/f/${formId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      setFormState(r.ok ? "sent" : "error");
    } catch {
      setFormState("error");
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: colors.bg, color: colors.text }}>
      {/* ── NAV ── */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 100,
          padding: "1.25rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "rgba(10,10,10,0.85)",
          backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(255,255,255,0.04)",
        }}
      >
        <span
          style={{
            fontFamily: fonts.mono,
            fontSize: "1.3rem",
            fontWeight: 700,
            letterSpacing: "0.15em",
            color: colors.text,
          }}
        >
          CARDO<span style={{ color: colors.amber }}>.</span>
        </span>
        <div style={{ display: "flex", gap: "2rem", alignItems: "center" }}>
          <button
            className="lp-nav-link"
            onClick={() => scrollTo("platform")}
            style={{
              ...btnReset,
              fontSize: "0.85rem",
              color: colors.text2,
              letterSpacing: "0.05em",
            }}
          >
            Platform
          </button>
          <button
            className="lp-nav-link"
            onClick={() => scrollTo("how")}
            style={{
              ...btnReset,
              fontSize: "0.85rem",
              color: colors.text2,
              letterSpacing: "0.05em",
            }}
          >
            How It Works
          </button>
          <a
            className="lp-nav-link"
            href="#demo"
            style={{
              fontSize: "0.85rem",
              color: colors.text2,
              textDecoration: "none",
              letterSpacing: "0.05em",
              cursor: "pointer",
            }}
          >
            Live Demo
          </a>
          <button
            onClick={() => scrollTo("pilot")}
            style={{
              ...btnReset,
              background: colors.amber,
              color: colors.bg,
              padding: "0.55rem 1.2rem",
              borderRadius: 4,
              fontWeight: 600,
              fontSize: "0.8rem",
              letterSpacing: "0.06em",
            }}
          >
            Request Pilot Access
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "8rem 2rem 4rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Ambient glow */}
        <div
          style={{
            position: "absolute",
            top: "-30%",
            right: "-10%",
            width: "60vw",
            height: "60vw",
            background: "radial-gradient(circle, rgba(232,145,45,0.15) 0%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        {/* Grid bg */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(232,145,45,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(232,145,45,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 100%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 70% 60% at 50% 40%, black 30%, transparent 100%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: 900, position: "relative", zIndex: 1 }}>
          <div
            className="anim-fade-up"
            style={{
              animationDelay: "0.2s",
              fontFamily: fonts.mono,
              fontSize: "0.75rem",
              color: colors.amber,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              marginBottom: "1.5rem",
            }}
          >
            Construction Intelligence Platform
          </div>
          <h1
            className="anim-fade-up"
            style={{
              animationDelay: "0.4s",
              fontSize: "clamp(2.8rem, 6vw, 5rem)",
              fontWeight: 700,
              lineHeight: 1.05,
              letterSpacing: "-0.03em",
              marginBottom: "1.5rem",
            }}
          >
            The central axis
            <br />
            of <em style={{ fontStyle: "normal", color: colors.amber }}>jobsite intelligence</em>
          </h1>
          <p
            className="anim-fade-up"
            style={{
              animationDelay: "0.6s",
              fontSize: "1.15rem",
              color: colors.text2,
              lineHeight: 1.65,
              maxWidth: 580,
              marginBottom: "2.5rem",
            }}
          >
            Cardo fuses computer vision, weather data, schedule context, and workforce signals into a
            single daily risk score &mdash; so you know exactly where to focus before someone gets
            hurt.
          </p>
          <div
            className="anim-fade-up"
            style={{
              animationDelay: "0.8s",
              display: "flex",
              gap: "1rem",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={() => scrollTo("pilot")}
              style={{
                ...btnReset,
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background: colors.amber,
                color: colors.bg,
                padding: "0.9rem 2rem",
                borderRadius: 4,
                fontWeight: 600,
                fontSize: "0.9rem",
                letterSpacing: "0.03em",
              }}
            >
              Request Pilot Access &rarr;
            </button>
            <a
              href="#demo"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                background: "transparent",
                color: colors.text,
                padding: "0.9rem 2rem",
                borderRadius: 4,
                fontWeight: 500,
                fontSize: "0.9rem",
                textDecoration: "none",
                letterSpacing: "0.03em",
                border: `1px solid ${colors.border}`,
              }}
            >
              Try Live Demo &rarr;
            </a>
          </div>
        </div>

        <ScoreCard />
      </section>

      {/* ── STATS ── */}
      <div
        className="lp-stats"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 1,
          background: colors.border,
          borderTop: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        {/* BLS CFOI 2024 — https://www.bls.gov/news.release/pdf/cfoi.pdf */}
        <a
          href="https://www.bls.gov/news.release/pdf/cfoi.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ background: colors.surface, padding: "2.5rem 2rem", textAlign: "center", textDecoration: "none", color: "inherit" }}
        >
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "2rem",
              fontWeight: 700,
              color: colors.text,
              marginBottom: "0.3rem",
            }}
          >
            5,070
          </div>
          <div style={{ fontSize: "0.8rem", color: colors.muted, letterSpacing: "0.03em" }}>
            Workers killed on the job, 2024
          </div>
        </a>
        {/* BLS CFOI 2024 — 1,032 construction / 5,070 total = 20.4% */}
        <a
          href="https://www.bls.gov/news.release/pdf/cfoi.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ background: colors.surface, padding: "2.5rem 2rem", textAlign: "center", textDecoration: "none", color: "inherit" }}
        >
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "2rem",
              fontWeight: 700,
              marginBottom: "0.3rem",
            }}
          >
            <span style={{ color: colors.amber }}>20%</span>
          </div>
          <div style={{ fontSize: "0.8rem", color: colors.muted, letterSpacing: "0.03em" }}>
            Of all worker fatalities are in construction
          </div>
        </a>
        {/* Midwest Economic Policy Institute (2017) */}
        <a
          href="https://midwestepi.org/2017/05/08/construction-fatalities-cost-the-us-5-billion-per-year/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ background: colors.surface, padding: "2.5rem 2rem", textAlign: "center", textDecoration: "none", color: "inherit" }}
        >
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "2rem",
              fontWeight: 700,
              color: colors.text,
              marginBottom: "0.3rem",
            }}
          >
            $5<span style={{ color: colors.amber }}>B</span>
          </div>
          <div style={{ fontSize: "0.8rem", color: colors.muted, letterSpacing: "0.03em" }}>
            Annual cost of construction fatalities
          </div>
        </a>
        {/* CPWR/BLS "Focus Four" hazards — https://www.cpwr.com/wp-content/uploads/DataBulletin-July2024.pdf */}
        <a
          href="https://www.cpwr.com/wp-content/uploads/DataBulletin-July2024.pdf"
          target="_blank"
          rel="noopener noreferrer"
          style={{ background: colors.surface, padding: "2.5rem 2rem", textAlign: "center", textDecoration: "none", color: "inherit" }}
        >
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "2rem",
              fontWeight: 700,
              color: colors.text,
              marginBottom: "0.3rem",
            }}
          >
            65<span style={{ color: colors.amber }}>%</span>
          </div>
          <div style={{ fontSize: "0.8rem", color: colors.muted, letterSpacing: "0.03em" }}>
            Of fatal injuries from four preventable hazards
          </div>
        </a>
      </div>

      {/* ── PLATFORM ── */}
      <section id="platform" style={{ padding: "6rem 2rem" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "0.7rem",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: colors.amber,
              marginBottom: "1rem",
            }}
          >
            Platform
          </div>
          <h2
            style={{
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              marginBottom: "1rem",
            }}
          >
            One platform. Every intelligence layer
            <br />
            your jobsite needs.
          </h2>
          <p
            style={{
              fontSize: "1.05rem",
              color: colors.text2,
              lineHeight: 1.6,
              maxWidth: 600,
              marginBottom: "3rem",
            }}
          >
            Starting with safety &mdash; expanding to schedule, quality, and equipment intelligence.
            Each module feeds the others.
          </p>

          <div
            className="lp-modules"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 1,
              background: colors.border,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              overflow: "hidden",
            }}
          >
            {MODULES.map((m) => (
              <div
                key={m.name}
                style={{
                  background: m.active ? colors.raised : colors.surface,
                  padding: "2.5rem 2rem",
                  position: "relative",
                }}
              >
                {m.active && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 2,
                      background: colors.amber,
                    }}
                  />
                )}
                <div style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>{m.icon}</div>
                <div style={{ fontWeight: 600, fontSize: "1.05rem", marginBottom: "0.6rem" }}>
                  {m.name}
                </div>
                <div style={{ fontSize: "0.85rem", color: colors.muted, lineHeight: 1.55 }}>
                  {m.desc}
                </div>
                <span
                  style={{
                    display: "inline-block",
                    marginTop: "1rem",
                    fontFamily: fonts.mono,
                    fontSize: "0.6rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    padding: "0.25rem 0.6rem",
                    borderRadius: 3,
                    background: m.active ? "rgba(76,175,125,0.15)" : "rgba(232,145,45,0.12)",
                    color: m.active ? colors.green : colors.amber,
                  }}
                >
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how" style={{ padding: "6rem 2rem", background: colors.surface }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            style={{
              fontFamily: fonts.mono,
              fontSize: "0.7rem",
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: colors.amber,
              marginBottom: "1rem",
            }}
          >
            How It Works
          </div>
          <h2
            style={{
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              marginBottom: "1rem",
            }}
          >
            From cameras to decisions
            <br />
            in under 60 seconds.
          </h2>
          <div
            className="lp-steps"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "3rem",
              marginTop: "2rem",
            }}
          >
            {STEPS.map((s) => (
              <div key={s.num}>
                <div
                  style={{
                    fontFamily: fonts.mono,
                    fontSize: "0.7rem",
                    color: colors.amber,
                    letterSpacing: "0.15em",
                    marginBottom: "0.75rem",
                  }}
                >
                  {s.num}
                </div>
                <div style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "0.6rem" }}>
                  {s.title}
                </div>
                <div style={{ fontSize: "0.88rem", color: colors.muted, lineHeight: 1.6 }}>
                  {s.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA / PILOT FORM ── */}
      <section
        id="pilot"
        style={{ textAlign: "center", padding: "6rem 2rem", position: "relative" }}
      >
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
            width: "50vw",
            height: "50vw",
            background: "radial-gradient(circle, rgba(232,145,45,0.15) 0%, transparent 65%)",
            pointerEvents: "none",
          }}
        />
        <h2
          style={{
            fontSize: "clamp(1.8rem, 3.5vw, 2.6rem)",
            fontWeight: 700,
            marginBottom: "1rem",
            position: "relative",
          }}
        >
          Now accepting pilot partners.
        </h2>
        <p
          style={{
            fontSize: "1rem",
            color: colors.text2,
            marginBottom: "2.5rem",
            position: "relative",
          }}
        >
          We&rsquo;re deploying with a select group of general contractors in the Pacific Northwest.
          Limited spots for 2026.
        </p>

        {formState === "sent" ? (
          <p
            style={{
              fontSize: "1.1rem",
              color: colors.amber,
              padding: "2rem 0",
              position: "relative",
            }}
          >
            Thanks &mdash; we&rsquo;ll be in touch within 48 hours.
          </p>
        ) : (
          <form
            onSubmit={handleSubmit}
            style={{ maxWidth: 480, margin: "0 auto", position: "relative" }}
          >
            <div
              className="lp-form-row"
              style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem" }}
            >
              <input
                className="lp-input"
                name="name"
                placeholder="Your name"
                required
                style={inputStyle}
              />
              <input
                className="lp-input"
                name="company"
                placeholder="Company"
                required
                style={inputStyle}
              />
            </div>
            <div
              className="lp-form-row"
              style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem" }}
            >
              <input
                className="lp-input"
                name="email"
                type="email"
                placeholder="Work email"
                required
                style={inputStyle}
              />
              <input
                className="lp-input"
                name="role"
                placeholder="Role / Title"
                style={inputStyle}
              />
            </div>
            <button
              type="submit"
              disabled={formState === "sending"}
              style={{
                width: "100%",
                marginTop: "0.25rem",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.5rem",
                background: colors.amber,
                color: colors.bg,
                padding: "0.9rem 2rem",
                borderRadius: 4,
                fontWeight: 600,
                fontSize: "0.9rem",
                letterSpacing: "0.03em",
                border: "none",
                cursor: formState === "sending" ? "default" : "pointer",
              }}
            >
              {formState === "sending" ? "Sending..." : "Request Pilot Access \u2192"}
            </button>
            {formState === "error" && (
              <p style={{ fontSize: "0.73rem", color: colors.red, marginTop: "0.75rem" }}>
                Something went wrong. Email fletcher.garrison@cardohq.build directly.
              </p>
            )}
            <p style={{ fontSize: "0.73rem", color: colors.muted, marginTop: "0.75rem" }}>
              We&rsquo;ll reach out within 48 hours. No spam, no sales deck &mdash; just a
              conversation about your jobsite.
            </p>
          </form>
        )}
      </section>

      {/* ── FOOTER ── */}
      <footer
        className="lp-footer"
        style={{
          borderTop: `1px solid ${colors.border}`,
          padding: "2.5rem 2rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ fontFamily: fonts.mono, fontSize: "0.75rem", color: colors.muted }}>
          &copy; 2026 CARDO &middot; cardohq.build
        </div>
        <div style={{ fontSize: "0.75rem", color: colors.muted }}>
          <a
            href="mailto:fletcher.garrison@cardohq.build"
            style={{ color: colors.text2, textDecoration: "none" }}
          >
            fletcher.garrison@cardohq.build
          </a>
        </div>
      </footer>
    </div>
  );
}
